import { supabase } from "@/integrations/supabase/client";
import { z } from 'zod';

const clientSnapshotSchema = z.object({
  success: z.literal(true),
  clients: z.array(z.object({ id: z.string(), isActiveClient: z.boolean() }).passthrough()),
  activeClientCount: z.number().int().nonnegative(),
});

export interface ClientData {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  birthDate?: string;
  clothingSize?: string;
  emergencyContact?: string;
  notes?: string;
  bypassCooldown?: boolean;
}

export interface SubscriptionData {
  id?: string;
  type: string;
  startDate: string;
  endDate: string;
  price: number;
  sessionsTotal?: number;
  sessionsRemaining?: number;
  jokersTotal?: number;
  jokersRemaining?: number;
  paymentFrequency?: string;
  paymentMethod?: string;
  isManuallyActivated?: boolean;
  status?: string;
}

/**
 * Fetch all clients for a gym
 */
export async function getClientsSnapshot(gymId: string, userIds?: string[] | null) {
  const { data, error } = await supabase.functions.invoke('client-management', {
    body: {
      action: 'get_clients',
      gym_id: gymId,
      user_ids: userIds,
    },
  });

  if (error) throw error;
  const result = clientSnapshotSchema.safeParse(data);
  if (!result.success) throw new Error('Failed to fetch clients');
  if (!userIds?.length) {
    const activeIds = new Set(result.data.clients.filter(c => c.isActiveClient).map(c => c.id));
    if (activeIds.size !== result.data.activeClientCount) throw new Error('Incomplete client list');
  }
  return result.data;
}

export async function getClients(gymId: string, userIds?: string[] | null) {
  return (await getClientsSnapshot(gymId, userIds)).clients;
}

/**
 * Fetch a single client by ID
 */
export async function getClientById(gymId: string, userId: string) {
  const { data, error } = await supabase.functions.invoke('client-management', {
    body: {
      action: 'get_client_by_id',
      gym_id: gymId,
      user_id: userId,
    },
  });

  if (error) throw error;
  if (!data.success) throw new Error(data.error || 'Failed to fetch client');

  return data.client;
}

/**
 * Update client profile
 */
export async function updateClient(gymId: string, userId: string, clientData: ClientData) {
  const { data, error } = await supabase.functions.invoke('client-management', {
    body: {
      action: 'update_client',
      gym_id: gymId,
      user_id: userId,
      client_data: clientData,
    },
  });

  if (error) throw error;
  if (!data.success) throw new Error(data.error || 'Failed to update client');

  return data;
}

/**
 * Delete a client
 */
export async function deleteClient(gymId: string, userId: string) {
  const { data, error } = await supabase.functions.invoke('client-management', {
    body: {
      action: 'delete_client',
      gym_id: gymId,
      user_id: userId,
    },
  });

  if (error) throw error;
  if (!data.success) throw new Error(data.error || 'Failed to delete client');

  return data;
}

/**
 * Update or create subscription
 */
export async function updateSubscription(gymId: string, userId: string, subscriptionData: SubscriptionData) {
  const { data, error } = await supabase.functions.invoke('client-management', {
    body: {
      action: 'update_subscription',
      gym_id: gymId,
      user_id: userId,
      subscription_data: subscriptionData,
    },
  });

  if (error) throw error;
  if (!data.success) throw new Error(data.error || 'Failed to update subscription');

  return data.subscription;
}
